import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { getMaintenanceDrag } from "@/common/services/maintenance.api";
import { IMaintenance, MAINTENANCE_STATUS } from "@/types/maintenance.type";
import { useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { AnimatePresence, motion } from "framer-motion";
import { GrHostMaintenance } from "react-icons/gr";
import { FaWrench } from "react-icons/fa";

const PopupNotification = ({ vehicle }: { vehicle: Vehicle; onClose: () => void }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.5 }}
      className="fixed bottom-6 right-6 bg-yellow-100 border-2 border-yellow-600 shadow-xl px-8 py-6 rounded-2xl z-50 text-center"
    >
      <div className="text-yellow-800 font-extrabold text-3xl">#{vehicle.waitingTicket}</div>
      <div className="text-yellow-700 text-lg">{vehicle.licensePlate}</div>
    </motion.div>
  );
};

type MaintenanceKanbanStatus =
  | MAINTENANCE_STATUS.Pending
  | MAINTENANCE_STATUS.UNDER_REVIEW
  | MAINTENANCE_STATUS.Finalize
  | MAINTENANCE_STATUS.Screening;

type Vehicle = {
  id: string;
  nome: string;
  status: MAINTENANCE_STATUS;
  licensePlate: string;
  updatedAt: string;
  waitingTicket: number;
};

const STATUS_LABELS: Record<MaintenanceKanbanStatus, string> = {
  [MAINTENANCE_STATUS.Pending]: "AGUARDANDO",
  [MAINTENANCE_STATUS.UNDER_REVIEW]: "EM MANUTENÇÂO",
  [MAINTENANCE_STATUS.Finalize]: "CONCLUÍDO",
  [MAINTENANCE_STATUS.Screening]: "TRIAGEM",
};

export default function MaintenancePainel() {
  const { notifyError } = useToastContext();
  const [screeningAndPendingVehicles, setScreeningAndPendingVehicles] = useState<Vehicle[]>([]);
  const [underReviewVehicles, setUnderReviewVehicles] = useState<Vehicle[]>([]);
  const [finalizedVehicles, setFinalizedVehicles] = useState<Vehicle[]>([]);
  const [popupVehicle, setPopupVehicle] = useState<Vehicle | null>(null);
  const [promoVehicle, setPromoVehicle] = useState<Vehicle | null>(null);

  const statusToSetter: Record<MaintenanceKanbanStatus, React.Dispatch<React.SetStateAction<Vehicle[]>>> = {
    [MAINTENANCE_STATUS.Screening]: setScreeningAndPendingVehicles,
    [MAINTENANCE_STATUS.Pending]: setScreeningAndPendingVehicles,
    [MAINTENANCE_STATUS.UNDER_REVIEW]: setUnderReviewVehicles,
    [MAINTENANCE_STATUS.Finalize]: setFinalizedVehicles,
  };

  useEffect(() => {
    async function loadMaintenance() {
      try {
        const result = await getMaintenanceDrag();
        if (!result.ok) return;
        const isToday = (dateInput: string | Date) => {
          const date = new Date(dateInput);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return date >= today;
        };

        const data = (result.data as IMaintenance[]).filter((item) => isToday(item.updatedAt)).map((item) => ({
          id: item._id!,
          nome: item.name,
          status: item.status,
          licensePlate: item.licensePlate,
          updatedAt: item.updatedAt.toString(),
          waitingTicket: item.waitingTicket ?? 0,
        }));

        const sortByDateAsc = (a: Vehicle, b: Vehicle) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        setScreeningAndPendingVehicles(
          [...data.filter((v) => v.status === MAINTENANCE_STATUS.Screening), ...data.filter((v) => v.status === MAINTENANCE_STATUS.Pending)]
            .sort(sortByDateAsc)
        );
        setUnderReviewVehicles(data.filter((v) => v.status === MAINTENANCE_STATUS.UNDER_REVIEW).sort(sortByDateAsc));
        setFinalizedVehicles(data.filter((v) => v.status === MAINTENANCE_STATUS.Finalize).sort(sortByDateAsc));
      } catch (error) {
        notifyError("Erro inesperado ao carregar manutenções");
      }
    }
    loadMaintenance();
  }, [notifyError]);

  useEffect(() => {
    const socket = io("https://localhost:3000", { transports: ["websocket"] });

    socket.on("maintenance:update", (updatedMaintenance: IMaintenance) => {
      const updated: Vehicle = {
        id: updatedMaintenance._id!,
        nome: updatedMaintenance.name,
        status: updatedMaintenance.status,
        licensePlate: updatedMaintenance.licensePlate,
        updatedAt: updatedMaintenance.updatedAt.toString(),
        waitingTicket: updatedMaintenance.waitingTicket ?? 0,
      };


      const previousVehicle =
        screeningAndPendingVehicles.find((v) => v.id === updated.id) ||
        underReviewVehicles.find((v) => v.id === updated.id) ||
        finalizedVehicles.find((v) => v.id === updated.id);

      const movedFromUnderReviewToFinalize =
        previousVehicle?.status === MAINTENANCE_STATUS.UNDER_REVIEW &&
        updated.status === MAINTENANCE_STATUS.Finalize;

      if (movedFromUnderReviewToFinalize) {
        setPromoVehicle(updated);
        playSound();
        setTimeout(() => setPromoVehicle(null), 7000);
      }


      const allStatuses: MaintenanceKanbanStatus[] = [
        MAINTENANCE_STATUS.Screening,
        MAINTENANCE_STATUS.Pending,
        MAINTENANCE_STATUS.UNDER_REVIEW,
        MAINTENANCE_STATUS.Finalize,
      ];

      allStatuses.forEach((status) => {
        statusToSetter[status]((prev) =>
          prev.some((v) => v.id === updated.id) ? prev.filter((v) => v.id !== updated.id) : prev
        );
      });

      statusToSetter[updated.status]((prev) => [updated, ...prev]);
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [underReviewVehicles, screeningAndPendingVehicles]);

  const containerRef = useRef<HTMLDivElement>(null);
  const handleEnterFullscreen = () => {
    const el = containerRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen();
  };

  const playSound = () => {
    const audio = new Audio("/sounds/new-notification-026-380249.mp3");
    audio.play();
  };

  return (
    <div ref={containerRef} className="min-h-screen font-sans bg-white">
      <div className="px-8 py-6">
        <h1 className="font-extrabold text-locavibe mb-5 tracking-wide">
          <GrHostMaintenance className="inline-block mr-2 text-4xl" onClick={handleEnterFullscreen} />
          Painel de Manutenção
        </h1>
        <div className="grid grid-cols-3 gap-4 mb-6 h-[calc(80vh-200px)] overflow-y-auto">
          <div className="col-span-1">
            <Column status={MAINTENANCE_STATUS.Pending} vehicles={screeningAndPendingVehicles} />
          </div>
          <div className="col-span-2">
            <Column status={MAINTENANCE_STATUS.UNDER_REVIEW} vehicles={underReviewVehicles} />
          </div>
        </div>

        <Column status={MAINTENANCE_STATUS.Finalize} vehicles={finalizedVehicles} />
        {popupVehicle && <PopupNotification vehicle={popupVehicle} onClose={() => setPopupVehicle(null)} />}
        {promoVehicle && <PromoCardModal vehicle={promoVehicle} />}
      </div>
    </div>
  );
}


function VehicleCard({ vehicle, status }: { vehicle: Vehicle; status: MaintenanceKanbanStatus }) {
  const plateColors: Record<MaintenanceKanbanStatus, string> = {
    [MAINTENANCE_STATUS.Pending]: "text-red-600",
    [MAINTENANCE_STATUS.UNDER_REVIEW]: "text-yellow-600",
    [MAINTENANCE_STATUS.Finalize]: "text-green-600",
    [MAINTENANCE_STATUS.Screening]: "text-red-600",
  };

  function cleanName(name: string): string {
    const ignoredWords = ["da", "do", "de", "das", "dos"];
    const nameParts = name.split(" ");
    const filteredNameParts = nameParts.filter(part => !ignoredWords.includes(part.toLowerCase()));
    const firstTwoNames = filteredNameParts.slice(0, 2).join(" ");
    return firstTwoNames;
  }

  const wrenchAnimation = status === MAINTENANCE_STATUS.UNDER_REVIEW ? {
    rotate: 360,
    x: [0, 10, 0],
    transition: {
      repeat: Infinity,
      repeatDelay: 0.5,
      duration: 1,
    },
  } : {};

  const pendingCardStyle = status === MAINTENANCE_STATUS.Pending ? "text-wrap" : "";
  const cleanedName = cleanName(vehicle.nome);
  const finalizedCardStyle = status === MAINTENANCE_STATUS.Finalize ? "w-[200px] h-[200px] text-sm" : "";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className={`bg-white flex justify-center items-center rounded-2xl shadow-md border border-gray-300 px-4 py-3 flex flex-col text-center relative overflow-hidden ${pendingCardStyle} ${finalizedCardStyle}`}
    >
      {status === MAINTENANCE_STATUS.UNDER_REVIEW && (
        <motion.div
          className="absolute top-2 left-2"
          style={{ fontSize: "40px", color: "#FFB800" }}
          animate={wrenchAnimation}
        >
          <FaWrench />
        </motion.div>
      )}

      <div className="absolute top-0 left-0 right-0 h-1 border-t border-dashed border-gray-300" />
      <div className="text-locavibe text-4xl font-extrabold drop-shadow-sm">
        {vehicle.licensePlate}
      </div>

      <div className={`text-gray-800 text-[20px] font-normal truncate uppercase ${pendingCardStyle}`}>
        {cleanedName}
      </div>

      <div className={`text-[30px] font-medium tracking-widest uppercase ${plateColors[status]}`}>
        #{vehicle.waitingTicket}
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-1 border-b border-dashed border-gray-300 z-10" />
    </motion.div>
  );
}


const Column = ({ status, vehicles }: { status: MaintenanceKanbanStatus; vehicles: Vehicle[] }) => {
  const isGrid = status === MAINTENANCE_STATUS.Pending || status === MAINTENANCE_STATUS.UNDER_REVIEW ? "grid grid-cols-1 md:grid-cols-3 gap-2 pt-4" : "";
  const bgColor = "bg-locavibe";

  // Ajuste do grid para a coluna "Finalizado"
  const gridStyle = status === MAINTENANCE_STATUS.Finalize
    ? "flex gap-2 pt-4"
    : "";

  return (
    <div className="bg-white rounded-2xl border border-red-300 p-5 w-full h-full">
      <h2 className={`${bgColor} text-white text-center text-3xl py-3 rounded-t-xl font-bold`}>
        {STATUS_LABELS[status]}
      </h2>
      <div className={`${isGrid} ${gridStyle}`}>
        <AnimatePresence>
          {vehicles.map((vehicle) => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} status={status} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};



const PromoCardModal = ({ vehicle }: { vehicle: Vehicle }) => {
  function speakText(text: string) {
    const synth = window.speechSynthesis;
    const voices = synth.getVoices()
    const ptVoices = voices.filter(v => v.lang.includes('pt'))
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "pt-BR";
    utterance.pitch = 1.2;
    utterance.rate = 1;
    utterance.volume = 1;
    if (ptVoices.length > 1) {
      utterance.voice = ptVoices[1];
    }
    synth.cancel();
    synth.speak(utterance);
  }

  useEffect(() => {
    const mensagem = `Senha número ${vehicle.waitingTicket}, placa ${vehicle.licensePlate}`;
    speakText(mensagem);
  }, [vehicle]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999] bg-black bg-opacity-80 flex items-center justify-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
        className="bg-yellow-400 border-[10px] border-red-600 text-center p-12 rounded-[50px] shadow-2xl w-[90%] max-w-[700px] relative overflow-hidden"
      >
        <motion.h1
          initial={{ scale: 0 }}
          animate={{ scale: [0.9, 1.1, 1] }}
          transition={{ duration: 1.2 }}
          className="text-[100px] text-red-800 font-extrabold drop-shadow-[2px_4px_2px_rgba(0,0,0,0.3)]"
        >
          SENHA #{vehicle.waitingTicket}
        </motion.h1>
        <p className="text-3xl font-semibold text-red-900 uppercase mt-2">{vehicle.nome}</p>
        <p className="text-4xl text-blue-900 font-bold mt-6">{vehicle.licensePlate}</p>

        <div className="absolute animate-ping-slow -top-10 -right-10 bg-white rounded-full w-40 h-40 opacity-10"></div>
        <div className="absolute animate-ping-slow -bottom-10 -left-10 bg-white rounded-full w-40 h-40 opacity-10"></div>
      </motion.div>
    </motion.div>
  );
};
